/* ============================================================================
   IDEA — Main JS (vanilla, no dependencies)
   ----------------------------------------------------------------------------
   Single deferred file. Modules:
     1. ScrollReveal     — IntersectionObserver-driven .idea-reveal
     2. Parallax         — rAF-driven, --idea-parallax CSS var
     3. Header           — sticky scroll-state, transparent on hero pages
     4. MobileMenu       — full-screen glass drawer
     5. CartDrawer       — Ajax add/update/remove with optimistic UI
     6. ProductGallery   — main image swap + thumbnail nav
     7. VariantPicker    — option selection → variant mapping → URL/UI sync
     8. Accordion        — native <details> enhancement
     9. Search           — predictive search dropdown
     10. AnnouncementMarquee — RTL/LTR aware infinite scroll
   ========================================================================= */

(function () {
  'use strict';

  const reducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const locale = (
    document.documentElement.lang ||
    (window.IDEA && window.IDEA.locale) ||
    'en'
  ).slice(0, 2).toLowerCase();
  const isArabic = locale === 'ar';
  const TEXT = {
    cartEmpty: isArabic ? 'السلة فارغة.' : 'Your cart is empty.',
    continueShopping: isArabic ? 'متابعة التسوق' : 'Continue Shopping',
    quantity: isArabic ? 'الكمية' : 'Quantity',
    increase: isArabic ? 'زيادة الكمية' : 'Increase quantity',
    decrease: isArabic ? 'إنقاص الكمية' : 'Decrease quantity',
    remove: isArabic ? 'إزالة' : 'Remove',
    addToCart: isArabic ? 'أضف إلى السلة' : 'Add to Cart',
    soldOut: isArabic ? 'نفذت الكمية' : 'Sold Out',
    unavailable: isArabic ? 'غير متوفر' : 'Unavailable',
  };

  function themeRoute(name, fallback) {
    return (window.IDEA && window.IDEA.routes && window.IDEA.routes[name]) || fallback;
  }

  function ajaxRoute(name, fallback) {
    const route = themeRoute(name, fallback);
    return route.endsWith('.js') ? route : `${route.replace(/\/$/, '')}.js`;
  }

  function cartPermalink(variantId, qty) {
    const cartRoute = themeRoute('cart', '/cart').replace(/\/$/, '');
    return `${cartRoute}/${variantId}:${qty}?checkout`;
  }

  function rootPath(path) {
    const rawRoot = themeRoute('root', '/') || '/';
    const root = rawRoot.endsWith('/') ? rawRoot : `${rawRoot}/`;
    return `${root}${path.replace(/^\//, '')}`;
  }

  /* ========================================================================
     1. SCROLL REVEAL
     ===================================================================== */
  function initReveal() {
    const targets = $$('.idea-reveal');
    if (!targets.length) return;
    if (reducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    targets.forEach(el => io.observe(el));
  }

  /* ========================================================================
     2. PARALLAX
     ===================================================================== */
  function initParallax() {
    if (reducedMotion) return;
    if (window.matchMedia('(max-width: 749px)').matches) return;
    const layers = $$('[data-idea-parallax]');
    if (!layers.length) return;

    let ticking = false;

    function update() {
      ticking = false;
      const vh = window.innerHeight;
      layers.forEach((layer) => {
        const rect = layer.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const speed = parseFloat(layer.dataset.ideaParallax) || 0.25;
        const center = rect.top + rect.height / 2;
        const delta  = (center - vh / 2) * speed * -1;
        const clamped = Math.max(-150, Math.min(150, delta));
        layer.style.setProperty('--idea-parallax', clamped.toFixed(1));
      });
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  /* ========================================================================
     3. HEADER scroll-state
     ===================================================================== */
  function initHeader() {
    const header = $('.idea-header');
    const bar    = $('.idea-announcement');
    if (!header) return;

    // Measure actual bar height and expose as CSS variable
    function syncBarHeight() {
      const h = bar ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--idea-bar-h', h + 'px');
    }
    syncBarHeight();
    window.addEventListener('resize', syncBarHeight, { passive: true });

    // Measure actual rendered header height and expose as CSS variable
    // so padding-top on sections is always pixel-perfect on every device
    function syncHeaderHeight() {
      document.documentElement.style.setProperty('--idea-header-h', header.offsetHeight + 'px');
    }
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight, { passive: true });

    const isTransparentMode = header.dataset.transparent === 'true';
    const glassThreshold    = 32;
    const hideThreshold     = 80;   // px before hide kicks in
    const delta             = 6;    // ignore tiny scroll jitter

    let lastY   = 0;
    let scrolled = false;
    let ticking  = false;

    function update() {
      const y = window.scrollY;

      // Glass state
      const isScrolled = y > glassThreshold;
      if (isScrolled !== scrolled) {
        scrolled = isScrolled;
        header.classList.toggle('idea-header--scrolled', scrolled);
      }

      // Hide / show on scroll direction (only past hideThreshold)
      if (y > hideThreshold) {
        const diff = y - lastY;
        if (diff > delta) {
          // scrolling down → hide
          header.classList.add('idea-header--hidden');
          if (bar) bar.classList.add('idea-announcement--hidden');
        } else if (diff < -delta) {
          // scrolling up → show
          header.classList.remove('idea-header--hidden');
          if (bar) bar.classList.remove('idea-announcement--hidden');
        }
      } else {
        // near top → always show
        header.classList.remove('idea-header--hidden');
        if (bar) bar.classList.remove('idea-announcement--hidden');
      }

      lastY   = y;
      ticking = false;
    }

    if (!isTransparentMode) {
      header.classList.add('idea-header--solid');
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  /* ========================================================================
     4. MOBILE MENU
     ===================================================================== */
  function initMobileMenu() {
    const toggle = $('[data-idea-mobile-toggle]');
    const drawer = $('.idea-mobile-drawer');
    const close  = $('[data-idea-mobile-close]');
    if (!toggle || !drawer) return;

    function open() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('idea-no-scroll');
      const firstLink = drawer.querySelector('a, button');
      if (firstLink) setTimeout(() => firstLink.focus(), 100);
    }
    function closeFn() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('idea-no-scroll');
      toggle.focus();
    }

    toggle.addEventListener('click', open);
    close && close.addEventListener('click', closeFn);
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) closeFn();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') closeFn();
    });
  }

  /* ========================================================================
     5. CART DRAWER (Ajax)
     ===================================================================== */
  const Cart = {
    drawer: null,
    overlay: null,
    body: null,
    countEls: [],

    init() {
      this.drawer  = $('.idea-cart-drawer');
      this.overlay = $('.idea-cart-overlay');
      this.body    = $('.idea-cart-drawer__body');
      this.countEls = $$('.idea-header__cart-count');
      if (!this.drawer) return;

      $$('[data-idea-cart-open]').forEach(btn => {
        btn.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });
      $('[data-idea-cart-close]')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      $$('form[action*="/cart/add"]').forEach(form => {
        form.addEventListener('submit', (e) => this.handleAdd(e, form));
      });

      this.drawer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-idea-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.updateItem(removeBtn.dataset.lineKey, 0);
          return;
        }
        const incBtn = e.target.closest('[data-idea-cart-inc]');
        if (incBtn) {
          this.adjustQty(incBtn.dataset.lineKey, 1);
          return;
        }
        const decBtn = e.target.closest('[data-idea-cart-dec]');
        if (decBtn) {
          this.adjustQty(decBtn.dataset.lineKey, -1);
          return;
        }
      });

      this.fetchCart().then(cart => this.updateCountUI(cart.item_count));
    },

    isOpen() { return this.drawer?.getAttribute('aria-hidden') === 'false'; },

    open() {
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('idea-no-scroll');
      this.refresh();
    },

    close() {
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('idea-no-scroll');
    },

    setLoading(state) {
      this.drawer?.classList.toggle('is-loading', state);
    },

    async handleAdd(e, form) {
      e.preventDefault();
      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn?.classList.add('is-loading');

      const formData = new FormData(form);
      try {
        const res = await fetch(ajaxRoute('cart_add', '/cart/add'), {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData,
        });
        if (!res.ok) throw new Error('Add to cart failed');
        await this.refresh();
        form.closest('[data-idea-quick-view]')?.close();
        this.open();
      } catch (err) {
        console.error('[IDEA] add to cart error', err);
        form.submit();
      } finally {
        submitBtn?.classList.remove('is-loading');
      }
    },

    async fetchCart() {
      const res = await fetch(ajaxRoute('cart', '/cart'), { headers: { 'Accept': 'application/json' }});
      return res.json();
    },

    async updateItem(key, qty) {
      this.setLoading(true);
      try {
        await fetch(ajaxRoute('cart_change', '/cart/change'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });
        await this.refresh();
      } catch (err) {
        console.error('[IDEA] cart update error', err);
      } finally {
        this.setLoading(false);
      }
    },

    async adjustQty(key, delta) {
      const input = this.drawer.querySelector(`[data-line-key="${key}"] input`);
      if (!input) return;
      const newQty = Math.max(0, parseInt(input.value, 10) + delta);
      input.value = newQty;
      await this.updateItem(key, newQty);
    },

    async refresh() {
      const cart = await this.fetchCart();
      this.render(cart);
      this.updateCountUI(cart.item_count);
    },

    updateCountUI(count) {
      this.countEls.forEach(el => {
        el.textContent = count;
        el.dataset.empty = count === 0 ? 'true' : 'false';
      });
    },

    render(cart) {
      if (!this.body) return;
      if (cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="idea-cart-drawer__empty">
            <svg class="idea-cart-drawer__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.4 7h13.4"/>
              <circle cx="9" cy="20" r="1.5" fill="currentColor"/>
              <circle cx="17" cy="20" r="1.5" fill="currentColor"/>
            </svg>
            <p>${TEXT.cartEmpty}</p>
            <a href="${rootPath('/collections/all')}" class="idea-btn idea-btn--ghost idea-btn--sm" style="margin-top:1.5rem;">
              ${TEXT.continueShopping}
            </a>
          </div>
        `;
        const footer = $('.idea-cart-drawer__footer');
        if (footer) footer.style.display = 'none';
        return;
      }

      const itemsHTML = cart.items.map(item => {
        /* ✅ Use featured_image (first image) instead of item.image (variant image) */
        const imgSrc = item.featured_image
          ? item.featured_image.url
            ? item.featured_image.url.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1$2')
            : item.featured_image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1$2')
          : (item.image
            ? item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1$2')
            : '');

        return `
        <div class="idea-cart-drawer__item" data-line-key="${item.key}">
          <img src="${imgSrc}"
               alt="${this.escapeHtml(item.product_title)}"
               class="idea-cart-drawer__item-img" loading="lazy">
          <div class="idea-cart-drawer__item-info">
            <div>
              <h3 class="idea-cart-drawer__item-title">${this.escapeHtml(item.product_title)}</h3>
              ${item.variant_title && item.variant_title !== 'Default Title'
                ? `<p class="idea-cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
            </div>
            <div class="idea-cart-drawer__qty" role="group" aria-label="${TEXT.quantity}">
              <button type="button" data-idea-cart-dec data-line-key="${item.key}" aria-label="${TEXT.decrease}">−</button>
              <input type="text" value="${item.quantity}" readonly aria-label="${TEXT.quantity}">
              <button type="button" data-idea-cart-inc data-line-key="${item.key}" aria-label="${TEXT.increase}">+</button>
            </div>
            <button type="button" class="idea-cart-drawer__remove" data-idea-cart-remove data-line-key="${item.key}">${TEXT.remove}</button>
          </div>
          <div class="idea-cart-drawer__item-price">${this.money(item.final_line_price)}</div>
        </div>
      `}).join('');

      this.body.innerHTML = itemsHTML;

      const footer = $('.idea-cart-drawer__footer');
      const subtotal = $('.idea-cart-drawer__subtotal-value');
      if (footer) footer.style.display = '';
      if (subtotal) subtotal.textContent = this.money(cart.total_price);
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    },

    money(cents) {
      const dollars = (cents / 100).toFixed(2);
      const symbol = (window.IDEA && window.IDEA.currencySymbol) || '$';
      return `${symbol}${dollars}`;
    },
  };

  /* ========================================================================
      6. PRODUCT GALLERY
      ===================================================================== */
  function initGallery() {
    const gallery = $('[data-idea-gallery]');
    if (!gallery) return;

    const mainWrap = gallery.querySelector('[data-idea-gallery-main-wrap]');
    const main = mainWrap && mainWrap.querySelector('img');
    if (!main) return;

    const thumbs = $$('.idea-product__thumb', gallery);
    let swapRequest = 0;

    function getCurrentIndex() {
      return thumbs.findIndex(t => t.dataset.mediaId === main.dataset.mediaId);
    }

    function swapImage(mediaId) {
      if (!thumbs.length) return;
      const thumb = thumbs.find(t => t.dataset.mediaId === String(mediaId));
      if (!thumb) return;

      const fullSrc = thumb.dataset.fullSrc;
      if (!fullSrc) return;

      const fullSrcset = thumb.dataset.fullSrcset || '';
      const alt = thumb.dataset.alt || main.alt || '';
      const requestId = ++swapRequest;

      if (main.dataset.mediaId === String(mediaId)) {
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        return;
      }

      const preload = new Image();
      preload.sizes = main.sizes || '(min-width: 990px) 50vw, 100vw';
      if (fullSrcset) preload.srcset = fullSrcset;
      preload.src = fullSrc;

      const commit = () => {
        if (requestId !== swapRequest) return;
        main.style.opacity = '0';
        window.setTimeout(() => {
          if (requestId !== swapRequest) return;
          if (fullSrcset) { main.srcset = fullSrcset; } else { main.removeAttribute('srcset'); }
          main.src = fullSrc;
          main.alt = alt;
          main.dataset.mediaId = String(mediaId);
          main.style.opacity = '1';
          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }, 110);
      };

      if (preload.decode) {
        preload.decode().then(commit).catch(commit);
      } else if (preload.complete) {
        commit();
      } else {
        preload.onload = commit;
        preload.onerror = commit;
      }
    }

    // Thumbnail clicks
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', e => {
        e.preventDefault();
        e.stopImmediatePropagation();
        swapImage(thumb.dataset.mediaId);
      });
    });

    // Prev/Next arrows on main gallery
    const galleryPrev = mainWrap.querySelector('[data-idea-gallery-prev]');
    const galleryNext = mainWrap.querySelector('[data-idea-gallery-next]');
    if (galleryPrev && thumbs.length) {
      galleryPrev.addEventListener('click', e => {
        e.stopPropagation();
        const prev = (getCurrentIndex() - 1 + thumbs.length) % thumbs.length;
        swapImage(thumbs[prev].dataset.mediaId);
      });
    }
    if (galleryNext && thumbs.length) {
      galleryNext.addEventListener('click', e => {
        e.stopPropagation();
        const next = (getCurrentIndex() + 1) % thumbs.length;
        swapImage(thumbs[next].dataset.mediaId);
      });
    }

    // Touch swipe on main gallery
    if (thumbs.length > 1) {
      let swipeStartX = 0;
      mainWrap.addEventListener('touchstart', e => { swipeStartX = e.changedTouches[0].clientX; }, { passive: true });
      mainWrap.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - swipeStartX;
        if (Math.abs(dx) < 50) return;
        const ci = getCurrentIndex();
        if (dx < 0) swapImage(thumbs[(ci + 1) % thumbs.length].dataset.mediaId);
        else        swapImage(thumbs[(ci - 1 + thumbs.length) % thumbs.length].dataset.mediaId);
      }, { passive: true });
    }

    // Lightbox
    const lightbox = $('[data-idea-lightbox]');
    if (lightbox) {
      const lbImg    = lightbox.querySelector('[data-idea-lightbox-img]');
      const lbPrev   = lightbox.querySelector('[data-idea-lightbox-prev]');
      const lbNext   = lightbox.querySelector('[data-idea-lightbox-next]');
      const lbClose  = lightbox.querySelector('[data-idea-lightbox-close]');
      const lbThumbs = $$('.idea-lightbox__thumb', lightbox);
      let lbIdx = 0;

      function lbSetImage(idx) {
        const src = lbThumbs.length ? lbThumbs : thumbs;
        if (!src.length) return;
        lbIdx = ((idx % src.length) + src.length) % src.length;
        const t = src[lbIdx];
        if (lbImg) {
          const nextSrc = t.dataset.fullSrc || '';
          if (lbImg.src !== nextSrc) {
            lbImg.style.opacity = '0';
            lbImg.style.transform = 'scale(0.97)';
            const preload = new Image();
            preload.src = nextSrc;
            const show = () => {
              lbImg.src = nextSrc;
              lbImg.alt = t.dataset.alt || '';
              lbImg.style.opacity = '1';
              lbImg.style.transform = 'scale(1)';
            };
            if (preload.decode) preload.decode().then(show).catch(show);
            else if (preload.complete) show();
            else { preload.onload = preload.onerror = show; }
          }
        }
        src.forEach((tb, i) => tb.classList.toggle('is-active', i === lbIdx));
        const active = src[lbIdx];
        active && active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      function openLightbox(mediaId) {
        // Show already-loaded gallery image instantly — zero black flash
        if (lbImg && main.src) {
          lbImg.src = main.src;
          lbImg.alt = main.alt;
          lbImg.style.opacity = '1';
          lbImg.style.transform = 'scale(1)';
        }
        lightbox.showModal();
        document.body.classList.add('idea-no-scroll');
        const src = lbThumbs.length ? lbThumbs : thumbs;
        let startIdx = 0;
        if (mediaId && src.length) {
          const found = src.findIndex(t => t.dataset.mediaId === String(mediaId));
          if (found >= 0) startIdx = found;
        }
        if (src.length) lbSetImage(startIdx);
      }

      // Preload high-res on desktop hover so click is instant
      mainWrap.addEventListener('mouseenter', () => {
        const ci = getCurrentIndex();
        const srcArr = lbThumbs.length ? lbThumbs : thumbs;
        const t = srcArr[ci >= 0 ? ci : 0];
        if (t && t.dataset.fullSrc) { const p = new Image(); p.src = t.dataset.fullSrc; }
      }, { passive: true });

      function closeLightbox() {
        lightbox.close();
        document.body.classList.remove('idea-no-scroll');
      }

      // Open on main image click
      mainWrap.addEventListener('click', e => {
        if (e.target.closest('[data-idea-gallery-prev],[data-idea-gallery-next]')) return;
        openLightbox(main.dataset.mediaId);
      });

      lbThumbs.forEach((t, i) => t.addEventListener('click', () => lbSetImage(i)));
      if (lbPrev) lbPrev.addEventListener('click', () => lbSetImage(lbIdx - 1));
      if (lbNext) lbNext.addEventListener('click', () => lbSetImage(lbIdx + 1));
      if (lbClose) lbClose.addEventListener('click', closeLightbox);

      lightbox.addEventListener('click', e => {
        if (!e.target.closest('.idea-lightbox__nav, .idea-lightbox__close, .idea-lightbox__thumb, .idea-lightbox__img, .idea-lightbox__strip'))
          closeLightbox();
      });
      lightbox.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  lbSetImage(lbIdx - 1);
        if (e.key === 'ArrowRight') lbSetImage(lbIdx + 1);
      });
      lightbox.addEventListener('close', () => document.body.classList.remove('idea-no-scroll'));

      // Touch swipe inside lightbox
      let lbSwipeX = 0;
      lightbox.addEventListener('touchstart', e => { lbSwipeX = e.changedTouches[0].clientX; }, { passive: true });
      lightbox.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].clientX - lbSwipeX;
        if (Math.abs(dx) < 50) return;
        dx < 0 ? lbSetImage(lbIdx + 1) : lbSetImage(lbIdx - 1);
      }, { passive: true });
    }

    main.style.transition = 'opacity 100ms ease, transform 600ms var(--idea-ease-out)';
  }

  /* ========================================================================
     7. VARIANT PICKER
     ===================================================================== */
  function initVariantPicker() {
    const picker = $('[data-idea-variant-picker]');
    if (!picker) return;
    const variants = JSON.parse(picker.dataset.variants || '[]');
    if (!variants.length) return;

    const optionInputs = $$('input[name^="option-"]', picker);
    const priceEl    = $('[data-idea-product-price]');
    const compareEl  = $('[data-idea-product-compare]');
    const atcBtn     = $('[data-idea-atc]');
    const variantIdInput = $('[data-idea-variant-id]');
    const buyNowBtn  = $('[data-idea-buy-now]');
    const qtyInput   = picker.querySelector('input[name="quantity"]');
    const atcLabels = {
      add: atcBtn?.dataset.labelAdd || TEXT.addToCart,
      soldOut: atcBtn?.dataset.labelSoldOut || TEXT.soldOut,
      unavailable: atcBtn?.dataset.labelUnavailable || TEXT.unavailable,
    };

    function getSelectedOptions() {
      const opts = [];
      optionInputs.forEach(input => {
        if (input.checked) {
          const idx = parseInt(input.dataset.optionIndex, 10);
          opts[idx] = input.value;
        }
      });
      return opts;
    }

    function findVariant(opts) {
      return variants.find(v => v.options.every((opt, i) => opt === opts[i]));
    }

    function update() {
      const opts = getSelectedOptions();
      const variant = opts.length ? findVariant(opts) : variants[0];
      if (!variant) {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.textContent = atcLabels.unavailable;
        }
        return;
      }

      if (variantIdInput) variantIdInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        atcBtn.textContent = variant.available ? atcLabels.add : atcLabels.soldOut;
      }

      if (history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }

      if (buyNowBtn) {
        const qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        if (variant.available) {
          buyNowBtn.href = cartPermalink(variant.id, qty);
          buyNowBtn.removeAttribute('aria-disabled');
          buyNowBtn.removeAttribute('tabindex');
        } else {
          buyNowBtn.setAttribute('aria-disabled', 'true');
          buyNowBtn.setAttribute('tabindex', '-1');
        }
      }
    }

    function formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      const symbol = (window.IDEA && window.IDEA.currencySymbol) || '$';
      return `${symbol}${dollars}`;
    }

    optionInputs.forEach(input => {
      input.addEventListener('change', () => {
        picker.querySelectorAll(`input[name="${input.name}"]`).forEach(inp => {
          inp.closest('.idea-product__option-value')?.classList.toggle('is-active', inp.checked);
        });
        const idx = input.dataset.optionIndex;
        const labelEl = picker.querySelector(`[data-option-selected-${idx}]`);
        if (labelEl) labelEl.textContent = input.value;
        update();
      });
    });

    if (qtyInput && buyNowBtn) {
      qtyInput.addEventListener('input', () => {
        const variant = findVariant(getSelectedOptions());
        if (!variant || !variant.available) return;
        const qty = parseInt(qtyInput.value, 10) || 1;
        buyNowBtn.href = cartPermalink(variant.id, qty);
      });
    }

    update();
  }

  /* ========================================================================
     8. ACCORDION (native <details> with smooth open/close)
     ===================================================================== */
  function initAccordion() {
    $$('details.idea-product__accordion-item').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;
    });
  }

  /* ========================================================================
     9. DESCRIPTION TOGGLE (read more / show less)
     ===================================================================== */
  function initDescToggle() {
    $$('[data-idea-desc-wrap]').forEach((wrap) => {
      const preview = wrap.querySelector('[data-idea-desc-preview]');
      const full    = wrap.querySelector('[data-idea-desc-full]');
      const btn     = wrap.querySelector('[data-idea-desc-toggle]');
      if (!preview || !full || !btn) return;

      const tmp    = document.createElement('div');
      tmp.innerHTML = full.innerHTML;
      const figure = tmp.querySelector('figure.table');
      const table  = figure || tmp.querySelector('table');

      if (table) {
        const prev = (figure || table).previousElementSibling;
        if (prev && /h[1-6]/i.test(prev.tagName)) preview.appendChild(prev.cloneNode(true));
        preview.appendChild((figure || table).cloneNode(true));
      } else {
        preview.classList.add('idea-product__description');
        preview.textContent = tmp.textContent.trim().slice(0, 200) + '…';
        btn.remove();
        return;
      }

      btn.addEventListener('click', () => {
        const showingPreview = !preview.hidden;
        preview.hidden = showingPreview;
        full.hidden    = !showingPreview;
        btn.querySelector('[data-idea-desc-label]').textContent = showingPreview ? btn.dataset.labelLess : btn.dataset.labelMore;
        btn.classList.toggle('is-open', showingPreview);
      });
    });
  }

  /* ========================================================================
     10. SEARCH (predictive)
     ===================================================================== */
  function initSearch() {
    const form = $('[data-idea-search-form]');
    if (!form) return;
    const input = form.querySelector('input[type="search"], input[name="q"]');
    const results = $('[data-idea-search-results]');
    if (!input || !results) return;

    let abortController = null;
    let timer = null;

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        results.style.display = 'none';
        return;
      }
      timer = setTimeout(async () => {
        if (abortController) abortController.abort();
        abortController = new AbortController();
        try {
          const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
          const res = await fetch(url, { signal: abortController.signal });
          const data = await res.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = `<p class="idea-search-empty">No matches for "${escapeHtml(q)}"</p>`;
          } else {
            results.innerHTML = products.map(p => `
              <a href="${p.url}" class="idea-search-result">
                ${p.featured_image
                  ? `<img src="${p.featured_image.url}" alt="${escapeHtml(p.title)}" loading="lazy">`
                  : ''}
                <div>
                  <div class="idea-search-result__title">${escapeHtml(p.title)}</div>
                  <div class="idea-search-result__price">${p.price}</div>
                </div>
              </a>
            `).join('');
          }
          results.style.display = '';
        } catch (err) {
          if (err.name !== 'AbortError') console.error('[IDEA] search error', err);
        }
      }, 250);
    });

    function escapeHtml(s) {
      const d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML;
    }
  }

  /* ========================================================================
     10. ANNOUNCEMENT MARQUEE
     ===================================================================== */
  function initMarquee() {
    // Content is duplicated in Liquid; animation runs through CSS.
  }

  /* ========================================================================
     11. STICKY CTA
     ===================================================================== */
  function initStickyCta() {
    const cta = $('.idea-sticky-cta');
    if (!cta) return;
    const showAfterScroll = cta.dataset.showAfterScroll === 'true';
    if (!showAfterScroll) {
      cta.classList.add('is-visible');
      return;
    }
    let last = 0;
    const threshold = window.innerHeight * 0.4;
    function update() {
      const y = window.scrollY;
      const visible = y > threshold;
      if (visible !== (last > threshold)) {
        cta.classList.toggle('is-visible', visible);
      }
      last = y;
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ========================================================================
     12. LAZY IMAGE FADE-IN
     ===================================================================== */
  function initLazyFade() {
    function markLoaded(img) {
      img.classList.add('is-loaded');
    }
    $$('.idea-product-card__img').forEach(markLoaded);
    $$('img[loading="lazy"]').forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
      } else {
        img.addEventListener('load', () => markLoaded(img), { once: true });
        img.addEventListener('error', () => markLoaded(img), { once: true });
      }
    });
  }

  /* ========================================================================
     13. QUICK VIEW
     ===================================================================== */
  function initQuickView() {
    $$('[data-idea-qv-src]').forEach((src) => {
      const target = src.previousElementSibling;
      if (!target || !target.hasAttribute('data-idea-qv-table')) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = src.innerHTML;
      const figure = tmp.querySelector('figure.table');
      const table  = figure || tmp.querySelector('table');
      if (table) {
        const heading = figure ? figure.previousElementSibling : table.closest('figure, div')?.previousElementSibling;
        if (heading && /h[1-6]/i.test(heading.tagName)) target.appendChild(heading.cloneNode(true));
        target.appendChild(figure || table);
      } else {
        target.className = 'idea-quick-view__desc';
        target.textContent = src.textContent.trim().slice(0, 150) + '…';
      }
    });

    $$('[data-idea-quick-view-open]').forEach((btn) => {
      if (btn.dataset.ideaQuickViewBound === 'true') return;
      btn.dataset.ideaQuickViewBound = 'true';

      btn.addEventListener('click', () => {
        const modal = document.getElementById(btn.getAttribute('aria-controls'));
        if (!modal) return;
        /* The dialog ships inside .idea-product-card, which is positioned and
           transformed. A non-modal dialog is laid out against that card instead
           of the viewport, so it lands at the card's top-right in RTL (top-left
           in LTR) instead of centered. Moving it to <body> makes both the modal
           and the fallback path center on the viewport. */
        if (modal.parentElement !== document.body) document.body.appendChild(modal);
        try {
          modal.showModal();
        } catch (err) {
          modal.classList.add('idea-quick-view--fallback');
          modal.setAttribute('open', '');
        }
        document.body.classList.add('idea-no-scroll');
      });
    });

    $$('[data-idea-quick-view]').forEach((modal) => {
      if (modal.dataset.ideaQuickViewBound === 'true') return;
      modal.dataset.ideaQuickViewBound = 'true';

      const close = () => {
        try {
          modal.close();
        } catch (err) {
          modal.removeAttribute('open');
        }
        modal.classList.remove('idea-quick-view--fallback');
        document.body.classList.remove('idea-no-scroll');
      };

      modal.querySelector('[data-idea-quick-view-close]')?.addEventListener('click', close);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) close();
      });
      modal.addEventListener('close', () => {
        modal.classList.remove('idea-quick-view--fallback');
        document.body.classList.remove('idea-no-scroll');
      });
    });
  }

  /* ========================================================================
     SEARCH OVERLAY
     ===================================================================== */
  function initSearchOverlay() {
    const overlay = document.getElementById('idea-search-overlay');
    if (!overlay) return;
    const input      = overlay.querySelector('[data-idea-search-input]');
    const resultsBox = overlay.querySelector('[data-idea-search-results]');
    const productsEl = overlay.querySelector('[data-idea-search-products]');
    const collectEl  = overlay.querySelector('[data-idea-search-collections]');
    const viewAllEl  = overlay.querySelector('[data-idea-search-view-all]');
    if (!input) return;

    let abortController = null;
    let timer = null;

    function openOverlay() {
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('idea-no-scroll');
      input.focus();
    }

    function closeOverlay() {
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('idea-no-scroll');
    }

    $$('[data-idea-search-open]').forEach(btn =>
      btn.addEventListener('click', openOverlay)
    );

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('[data-idea-search-close]')) closeOverlay();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.getAttribute('aria-hidden') === 'false') closeOverlay();
    });

    function escapeHtml(s) {
      const d = document.createElement('div');
      d.textContent = s;
      return d.innerHTML;
    }

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) {
        if (resultsBox) resultsBox.style.display = 'none';
        return;
      }
      timer = setTimeout(async () => {
        if (abortController) abortController.abort();
        abortController = new AbortController();
        try {
          const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=4`;
          const res  = await fetch(url, { signal: abortController.signal });
          const data = await res.json();
          const products    = data?.resources?.results?.products    || [];
          const collections = data?.resources?.results?.collections || [];

          if (productsEl) {
            productsEl.innerHTML = products.map(p => `
              <a href="${p.url}" class="idea-search-product">
                ${p.featured_image
                  ? `<img src="${p.featured_image.url}" alt="${escapeHtml(p.title)}" loading="lazy">`
                  : ''}
                <div class="idea-search-product__title">${escapeHtml(p.title)}</div>
                <div class="idea-search-product__price">${p.price}</div>
              </a>
            `).join('');
          }

          if (collectEl) {
            collectEl.innerHTML = collections.map(c =>
              `<a href="${c.url}" class="idea-search-collection">${escapeHtml(c.title)}</a>`
            ).join('');
          }

          if (viewAllEl) {
            viewAllEl.href = `/search?q=${encodeURIComponent(q)}`;
            viewAllEl.style.display = '';
          }
          if (resultsBox) resultsBox.style.display = (products.length || collections.length) ? '' : 'none';
        } catch (err) {
          if (err.name !== 'AbortError') console.error('[IDEA] search overlay error', err);
        }
      }, 250);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const q = input.value.trim();
        if (q) window.location.href = rootPath(`/search?q=${encodeURIComponent(q)}`);
      }
    });
  }

  /* ========================================================================
     14. SLIDESHOW
     ===================================================================== */
  function initSlideshow() {
    $$('[data-idea-slideshow]').forEach((el) => {
      const slides = $$('.idea-slideshow__slide', el);
      const dots   = $$('[data-idea-slide-dot]', el);
      if (slides.length < 2) return;

      let current  = 0;
      let timer    = null;
      const autoplay = el.dataset.autoplay === 'true';
      const speed    = parseInt(el.dataset.speed, 10) || 5000;

      function goTo(index) {
        slides[current].classList.remove('is-active');
        if (dots[current]) { dots[current].classList.remove('is-active'); dots[current].setAttribute('aria-selected', 'false'); }
        current = (index + slides.length) % slides.length;
        slides[current].classList.add('is-active');
        if (dots[current]) { dots[current].classList.add('is-active'); dots[current].setAttribute('aria-selected', 'true'); }
      }

      function startAuto() {
        if (!autoplay) return;
        timer = setInterval(() => goTo(current + 1), speed);
      }
      function resetAuto() { clearInterval(timer); startAuto(); }

      el.querySelector('[data-idea-slide-prev]')?.addEventListener('click', () => { goTo(current - 1); resetAuto(); });
      el.querySelector('[data-idea-slide-next]')?.addEventListener('click', () => { goTo(current + 1); resetAuto(); });
      dots.forEach((dot) => {
        dot.addEventListener('click', () => { goTo(parseInt(dot.dataset.ideaSlideDot, 10)); resetAuto(); });
      });

      const rtlDir = document.documentElement.dir === 'rtl' ? -1 : 1;
      let touchStartX = 0;
      el.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      el.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) { goTo(current + (diff > 0 ? rtlDir : -rtlDir)); resetAuto(); }
      }, { passive: true });

      let dragStartX = 0;
      let isDragging = false;
      let dragMoved = false;

      el.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        dragStartX = e.clientX;
        isDragging = true;
        dragMoved = false;
        el.style.cursor = 'grabbing';
      });

      el.addEventListener('dragstart', (e) => { e.preventDefault(); });

      window.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        if (Math.abs(e.clientX - dragStartX) > 5) dragMoved = true;
      });

      window.addEventListener('mouseup', (e) => {
        if (!isDragging) return;
        isDragging = false;
        el.style.cursor = '';
        const diff = dragStartX - e.clientX;
        if (Math.abs(diff) > 50) { goTo(current + (diff > 0 ? rtlDir : -rtlDir)); resetAuto(); }
      });

      el.addEventListener('click', (e) => {
        if (dragMoved) e.preventDefault();
      }, true);

      startAuto();
    });
  }

  /* ========================================================================
     15. MEGA MENU
     ===================================================================== */
  function initMegaMenu() {
    $$('[data-idea-megamenu-trigger]').forEach((trigger) => {
      const item = trigger.closest('.idea-header__nav-item--mega');
      if (!item) return;
      const menu = item.querySelector('.idea-megamenu');
      if (!menu) return;

      item.addEventListener('mouseenter', () => trigger.setAttribute('aria-expanded', 'true'));
      item.addEventListener('mouseleave', () => trigger.setAttribute('aria-expanded', 'false'));

      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const expanded = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', String(!expanded));
        }
        if (e.key === 'Escape') trigger.setAttribute('aria-expanded', 'false');
      });
    });

    $$('[data-idea-mobile-mega]').forEach((toggle) => {
      const panel = toggle.nextElementSibling;
      if (!panel) return;
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.classList.toggle('is-open', !open);
      });
    });
  }

  /* ========================================================================
     BOOT
     ===================================================================== */
  function boot() {
    initReveal();
    initParallax();
    initHeader();
    initMobileMenu();
    initMarquee();
    initStickyCta();
    initLazyFade();
    initGallery();
    initVariantPicker();
    initAccordion();
    initDescToggle();
    initSearch();
    initSearchOverlay();
    initQuickView();
    initSlideshow();
    initMegaMenu();
    Cart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.IDEA = window.IDEA || {};
  window.IDEA.reinit = boot;

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
  }
})();
